import { Check } from 'lucide-react'
import { cn } from '../../lib/cn'

interface ChipProps {
  children: React.ReactNode
  selected: boolean
  onToggle: () => void
  disabled?: boolean
  /** 'press' (multiselect, aria-pressed) | 'check' (single-select radio-like). */
  mode?: 'press' | 'check'
  icon?: React.ReactNode
  className?: string
}

/**
 * The shared selection control for vibe chips, coverage stops, togetherness
 * modes, and feedback reasons. Selection language is additive (never color-only):
 * ember ring + warm tint + a check/dot icon (design-system §6, AX §7).
 */
export function Chip({
  children,
  selected,
  onToggle,
  disabled,
  mode = 'press',
  icon,
  className,
}: ChipProps) {
  return (
    <button
      type="button"
      {...(mode === 'press' ? { 'aria-pressed': selected } : { 'aria-checked': selected, role: 'radio' })}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        // whitespace-nowrap: same one-line-pill rule as Badge — rows wrap, pills don't.
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-chip px-3 py-1.5 text-sm font-medium transition duration-150 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        selected
          ? 'bg-ember-500/15 text-ember-300 ring-1 ring-ember-400/60'
          : 'bg-surface text-text-muted ring-1 ring-border hover:text-text hover:ring-border-warm',
        className,
      )}
    >
      {icon && (
        <span className="inline-flex shrink-0" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
      {selected && !icon && <Check className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />}
    </button>
  )
}
